"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileText, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WorkspaceFile } from "@/lib/workspace-context";

export interface WikiLinkSuggestion {
  file: WorkspaceFile;
  /** Indica si el archivo existe o se crearía al confirmar. */
  exists: boolean;
}

interface WikiLinkAutocompleteProps {
  query: string;
  position: { x: number; y: number } | null;
  suggestions: WikiLinkSuggestion[];
  onSelect: (suggestion: WikiLinkSuggestion) => void;
  onClose: () => void;
}

/**
 * Popover de autocompletado para `[[wiki-links]]`.
 * Aparece al tipear `[[` y muestra fuzzy match sobre los títulos existentes.
 * Tecla `Enter` selecciona, `Esc` cierra, `↑↓` navega.
 * Si ningún match existe, ofrece crear uno nuevo.
 */
export function WikiLinkAutocomplete({
  query,
  position,
  suggestions,
  onSelect,
  onClose,
}: WikiLinkAutocompleteProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  useEffect(() => setSelectedIndex(0), [query]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => (i + 1) % Math.max(suggestions.length, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => (i - 1 + suggestions.length) % Math.max(suggestions.length, 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const sel = suggestions[selectedIndex];
        if (sel) onSelect(sel);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [suggestions, selectedIndex, onSelect, onClose]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!mounted || !position || suggestions.length === 0) return null;

  return createPortal(
    <div
      ref={ref}
      role="listbox"
      style={{ left: position.x, top: position.y }}
      className="fixed z-[250] w-72 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-md shadow-xl p-1.5 animate-in fade-in zoom-in-95 duration-100"
    >
      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-1">
        Wiki-links {query && `(buscando "${query}")`}
      </div>
      <div className="max-h-60 overflow-y-auto custom-scrollbar">
        {suggestions.map((s, i) => {
          const name = s.file.name.replace(".md", "");
          return (
            <button
              key={s.file.id}
              role="option"
              aria-selected={i === selectedIndex}
              onMouseEnter={() => setSelectedIndex(i)}
              onClick={() => onSelect(s)}
              className={cn(
                "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors",
                i === selectedIndex
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary/60",
              )}
            >
              {s.exists ? (
                <FileText
                  className={cn(
                    "w-3.5 h-3.5 shrink-0",
                    i === selectedIndex ? "text-primary-foreground" : "text-muted-foreground/70",
                  )}
                  strokeWidth={1.75}
                />
              ) : (
                <Plus
                  className={cn(
                    "w-3.5 h-3.5 shrink-0",
                    i === selectedIndex ? "text-primary-foreground" : "text-success",
                  )}
                  strokeWidth={2}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate">{name}</div>
                {!s.exists && (
                  <div
                    className={cn(
                      "text-[10px]",
                      i === selectedIndex ? "text-primary-foreground/80" : "text-success/80",
                    )}
                  >
                    Crear nueva nota
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Genera sugerencias fuzzy match para un query de wiki-link.
 * Si no hay matches, devuelve una sola sugerencia para crear nueva nota.
 */
export function buildWikiLinkSuggestions(
  query: string,
  files: WorkspaceFile[],
  maxResults: number = 8
): WikiLinkSuggestion[] {
  const cleaned = query.toLowerCase().trim();
  if (cleaned.length === 0) return [];

  const fileMatches = files
    .filter((f) => f.type === "file")
    .map((f) => {
      const name = f.name.replace(".md", "").toLowerCase();
      let score = 0;
      if (name === cleaned) score = 100;
      else if (name.startsWith(cleaned)) score = 80;
      else if (name.includes(cleaned)) score = 60;
      return { file: f, score };
    })
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map((m) => ({ file: m.file, exists: true }));

  if (fileMatches.length > 0) return fileMatches;
  if (cleaned.length === 0) return [];

  // No matches: offer to create
  return [
    {
      file: { id: `__new__${cleaned}`, name: `${cleaned}.md`, type: "file", content: "" } as WorkspaceFile,
      exists: false,
    },
  ];
}
